import type { BillingVendor } from '@/services/auth/types';
import { isEmptyString } from '@/utils/string';
import { BillingSystem } from './types';

/**
 * The account fields the vendor resolution reads. Both are widened to accept `null` so any
 * account-shaped object can be passed without pre-cleaning; `isEmptyString` treats null, undefined and
 * blank alike.
 */
type VendorSourceAccount = {
  billing_vendor?: BillingVendor | null;
  ordway_id?: string | null;
};

/**
 * The account's billing vendor, resolving the case where the API sends none.
 *
 * `billing_vendor` is optional on the wire: the column is `DEFAULT NULL`
 * (`services/schemas/customerio/accounts.sql:88`) with no backfill, and
 * `services/ui_api/accounts/dom.go:386` serializes the raw column, so accounts predating
 * `domains/accounts/create.go:358` send no vendor and `omitempty` drops the key.
 *
 * The backend does not treat that as unknown. `Account.Vendor()`
 * (`services/domains/accounts/billing_vendor.go:13`) resolves it — an explicit column wins, otherwise
 * an `ordway_id` means Ordway, otherwise legacy Stripe — and that resolved value is what
 * `billing/subscriptions/pause` gates on (`ui_api/billing/pause.go:66`) and what FlightDeck serializes
 * (`cmds/flightdeck_api/flightdeck/accounts_find.go:1450`). Only the `ui_api` account payload exposes
 * the raw column, so this mirror exists to keep the UI's answer the same as the server's.
 *
 * Without it, an account with no vendor and no `ordway_id` is one the backend would happily pause while
 * the UI hides the action — silently, because both pause guards fail closed.
 *
 * **Delete this once `ui_api/accounts/dom.go` sends `string(account.Vendor())`.** The field is then
 * always present and callers can compare it directly.
 */
export function resolveBillingVendor(account: VendorSourceAccount): BillingVendor {
  if (!isEmptyString(account.billing_vendor)) {
    return account.billing_vendor;
  }
  if (!isEmptyString(account.ordway_id)) {
    return BillingSystem.Ordway;
  }
  return BillingSystem.Stripe;
}

/**
 * Whether the account carries an explicit `billing_vendor`, rather than one inferred by
 * {@link resolveBillingVendor}. Only for surfaces that report the vendor to a human — the masquerade
 * banner says so, because "the record says Stripe" and "there is no record, so we assume Stripe" are
 * different facts for support.
 */
export function hasExplicitBillingVendor(account: VendorSourceAccount): boolean {
  return !isEmptyString(account.billing_vendor);
}
